import { asPlaybookId } from "@repo/app-types/entities/playbook";
import { asTeammateId } from "@repo/app-types/entities/teammate";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { DateTime } from "luxon";
import { type FC, useMemo, useState } from "react";
import { useParams } from "react-router";
import {
  type PerformanceAchievementQuarter,
  usePlaybookTeammatePerformanceAchievementsQuery,
} from "../../api/entities/performancePlaybook.js";
import { PageBody } from "../../components/layout/pageLayout.js";
import { AchievementsLeaderboard } from "./achievementsLeaderboard.js";
import { AchievementsPerfectCalls } from "./achievementsPerfectCalls.js";

function formatQuarterLabel(startDate: string): string {
  const dt = DateTime.fromISO(startDate);
  return `Q${dt.quarter} ${dt.year}`;
}

export const TrainingTeammatePageAchievements: FC = () => {
  const { playbookId: playbookIdParam, teammateId: teammateIdParam } =
    useParams();
  if (!playbookIdParam || !teammateIdParam) {
    throw new Error("Missing param playbookId or teammateId.");
  }
  const playbookId = asPlaybookId(playbookIdParam);

  const teammateId = asTeammateId(teammateIdParam);

  const { data: quartersAchievements, isLoading: isLoadingAchievements } =
    usePlaybookTeammatePerformanceAchievementsQuery({
      playbookId,
      teammateId,
    });

  const quarterlyPeriodOptions = useMemo<
    readonly SimpleSelectOption<string>[]
  >(() => {
    if (!quartersAchievements || quartersAchievements.length === 0) {
      return [];
    }
    return quartersAchievements.map((q) => ({
      label: formatQuarterLabel(q.start_date),
      value: q.start_date,
    }));
  }, [quartersAchievements]);

  const [selectedQuarter, setSelectedQuarter] = useState<string | undefined>();

  // Default to the most recent quarter
  const effectiveSelectedQuarter =
    selectedQuarter ?? quarterlyPeriodOptions.at(-1)?.value;

  const selectedQuarterData = useMemo<
    PerformanceAchievementQuarter | undefined
  >(
    () =>
      quartersAchievements?.find(
        (q) => q.start_date === effectiveSelectedQuarter,
      ),
    [quartersAchievements, effectiveSelectedQuarter],
  );

  const perfectCalls = useMemo(
    () =>
      quartersAchievements?.reduce(
        (sum, q) => sum + q.achievements.nb_perfect_calls,
        0,
      ) ?? 0,
    [quartersAchievements],
  );

  if (isLoadingAchievements) {
    return (
      <div className="flex justify-center py-12">
        <Loader style={LoaderStyle.SPINNER} />
      </div>
    );
  }

  return (
    <PageBody>
      <section className="space-y-6">
        <section className="space-y-3">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-foreground">
              Leaderboard
            </h2>
            <SimpleSelect
              label="Quarter"
              isLabelHidden
              options={quarterlyPeriodOptions}
              value={effectiveSelectedQuarter ?? ""}
              onChange={setSelectedQuarter}
            />
          </div>
          <AchievementsLeaderboard selectedQuarterData={selectedQuarterData} />
        </section>

        <section className="space-y-3">
          <AchievementsPerfectCalls
            perfectCalls={perfectCalls}
            quarterHistory={quartersAchievements ?? []}
          />
        </section>
      </section>
    </PageBody>
  );
};
